import React, { useEffect, useState, useRef } from 'react';
import { Search, PenLine, ListChecks, CheckCircle, Clock, Shield, Home, Building2, GitCompareArrows, Scale } from 'lucide-react';
import type { ReviewProgress } from '../services/edgeFunctionClient';
import type { ServiceType } from '../services/serviceOrderService';

interface ReviewProcessingLoaderProps {
  serviceType: ServiceType;
  progress: ReviewProgress | null;
}

const tenancyStages = [
  {
    icon: Search,
    title: 'Analysing Document',
    description: 'Checking legal compliance and identifying issues',
  },
  {
    icon: PenLine,
    title: 'Rewriting Document',
    description: 'Creating your compliant legal document',
  },
  {
    icon: ListChecks,
    title: 'Generating Annotations',
    description: 'Comparing changes and documenting reasons',
  },
  {
    icon: CheckCircle,
    title: 'Finalising',
    description: 'Assembling your complete review package',
  },
];

const commercialStages = [
  {
    icon: Search,
    title: 'Reading & Analysing Both Documents',
    description: 'Reviewing every provision, translating legal jargon, identifying risks',
  },
  {
    icon: GitCompareArrows,
    title: 'Comparing Clauses & Generating Redlines',
    description: 'Clause-by-clause comparison with recommendations',
  },
  {
    icon: Scale,
    title: 'Preparing Negotiation Strategy',
    description: 'Building your business-specific advice and priorities',
  },
];

const SERVICE_CONFIG: Record<ServiceType, {
  title: string;
  gradient: string;
  icon: React.FC<{ className?: string }>;
  tips: string[];
  heroImage: string;
  heroTagline: string;
  overlayGradient: string;
}> = {
  tenancy_review_rewrite: {
    title: 'Reviewing Your Tenancy Agreement',
    gradient: 'from-gray-900 to-gray-800',
    icon: Home,
    tips: [
      'The Renters\' Rights Bill 2025 abolishes Section 21 "no-fault" evictions across England.',
      'All new tenancies will be periodic (rolling monthly) by default from May 2026.',
      'Landlords must meet the Decent Homes Standard, which now applies to the private rented sector.',
      'Awaab\'s Law requires landlords to address damp and mould hazards within strict timeframes.',
      'Tenants can now request a pet, and landlords cannot unreasonably refuse.',
    ],
    heroImage: 'https://images.unsplash.com/photo-1558036117-15d82a90b9b1?w=800&q=80',
    heroTagline: 'Building a compliant, fair tenancy agreement for you',
    overlayGradient: 'from-black/80 via-gray-900/40 to-gray-800/60',
  },
  commercial_lease_review: {
    title: 'Reviewing Your Commercial Lease',
    gradient: 'from-gray-900 to-gray-800',
    icon: Building2,
    tips: [
      'The Lease Code 2007 recommends that break clauses should not require strict compliance with all lease terms.',
      'Under the Landlord & Tenant Act 1954, tenants have an automatic right to renew their lease unless specifically excluded.',
      'Service charges in commercial leases should be reasonable and transparent, following RICS best practice.',
      'Upward-only rent review clauses remain common but can be heavily disadvantageous to tenants.',
      'Alienation clauses should allow assignment and subletting with landlord consent not to be unreasonably withheld.',
    ],
    heroImage: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80',
    heroTagline: 'Analysing every clause to protect your business interests',
    overlayGradient: 'from-black/80 via-gray-900/40 to-gray-800/60',
  },
};

export const ReviewProcessingLoader: React.FC<ReviewProcessingLoaderProps> = ({
  serviceType,
  progress,
}) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [tipIndex, setTipIndex] = useState(0);
  const startTimeRef = useRef(Date.now());
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const tipTimerRef = useRef<ReturnType<typeof setInterval>>();
  const isCompleteRef = useRef(false);
  const config = SERVICE_CONFIG[serviceType];

  useEffect(() => {
    startTimeRef.current = Date.now();

    timerRef.current = setInterval(() => {
      if (!isCompleteRef.current) {
        setElapsedTime(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }
    }, 1000);

    tipTimerRef.current = setInterval(() => {
      setTipIndex((prev) => (prev + 1) % config.tips.length);
    }, 12000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (tipTimerRef.current) clearInterval(tipTimerRef.current);
    };
  }, [config.tips.length]);

  useEffect(() => {
    if (progress?.status === 'complete') {
      isCompleteRef.current = true;
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, [progress?.status]);

  const currentStage = progress ? progress.stage - 1 : 0;
  const percent = progress?.percent ?? 0;
  const ServiceIcon = config.icon;

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const formatWords = () => {
    if (!progress?.streamedChars) return null;
    const approxWords = Math.round(progress.streamedChars / 5.5);
    if (approxWords < 1000) return `${approxWords} words`;
    return `${(approxWords / 1000).toFixed(1)}k words`;
  };

  const circumference = 2 * Math.PI * 48;
  const strokeDashoffset = circumference - (percent / 100) * circumference;

  return (
    <div className="fixed inset-0 bg-gray-50 z-50 overflow-auto">
      <div className="absolute inset-0 bg-dot-pattern opacity-50" />

      <div className="relative z-10 min-h-full flex items-center justify-center lg:justify-start">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid lg:grid-cols-5 gap-8 items-center">
            <div className="lg:col-span-3 flex justify-center lg:justify-start">
              <div className="max-w-lg w-full animate-scale-in">
                <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-elevated-lg border border-gray-200">
                  <div className="flex flex-col items-center mb-8">
                    <div className="relative w-28 h-28 mb-5">
                      <svg className="w-28 h-28 -rotate-90" viewBox="0 0 112 112">
                        <circle cx="56" cy="56" r="48" fill="none" stroke="#f3f4f6" strokeWidth="6" />
                        <circle
                          cx="56" cy="56" r="48" fill="none"
                          stroke="url(#progressGradient)"
                          strokeWidth="6"
                          strokeLinecap="round"
                          strokeDasharray={circumference}
                          strokeDashoffset={strokeDashoffset}
                          className="transition-all duration-700 ease-out"
                        />
                        <defs>
                          <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#111827" />
                            <stop offset="100%" stopColor="#374151" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${config.gradient} flex items-center justify-center shadow-lg`}>
                          <ServiceIcon className="w-7 h-7 text-white" />
                        </div>
                      </div>
                    </div>

                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 text-center">
                      {config.title}
                    </h2>
                    <p className="text-gray-500 text-sm text-center">
                      {elapsedTime > 600
                        ? 'Nearing completion. Complex documents require thorough analysis. Thank you for your patience.'
                        : elapsedTime > 300
                        ? 'Processing a complex document. This is normal and may take up to 15 minutes total.'
                        : elapsedTime > 60
                        ? 'Still working... Larger documents can take up to 15 minutes to fully process.'
                        : 'This process can take up to 15 minutes. Please be patient.'}
                    </p>
                  </div>

                  <div className="mb-7">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Progress</span>
                      <div className="flex items-center gap-3">
                        {formatWords() && (
                          <span className="text-xs text-gray-400">{formatWords()}</span>
                        )}
                        <span className="text-sm font-bold text-gray-900">{Math.round(percent)}%</span>
                      </div>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full bg-gradient-to-r ${config.gradient} transition-all duration-700 ease-out`}
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </div>

                  <div className="relative mb-7">
                    <div className="absolute left-[14px] top-4 bottom-4 w-px bg-gray-100" />

                    <div className="space-y-1">
                      {(serviceType === 'commercial_lease_review' ? commercialStages : tenancyStages).map((stage, index) => {
                        const isActive = index === currentStage;
                        const isComplete = index < currentStage || (index === currentStage && progress?.status === 'complete');
                        const StageIcon = stage.icon;

                        return (
                          <div
                            key={index}
                            className={`relative flex items-center gap-3.5 px-3 py-3 rounded-xl transition-all duration-500 ${
                              isActive ? 'bg-gray-50' : ''
                            }`}
                          >
                            <div className="flex-shrink-0 relative z-10">
                              {isComplete ? (
                                <div className="w-7 h-7 bg-gray-900 rounded-lg flex items-center justify-center">
                                  <CheckCircle className="w-4 h-4 text-white" />
                                </div>
                              ) : isActive ? (
                                <div className={`w-7 h-7 bg-gradient-to-br ${config.gradient} rounded-lg flex items-center justify-center animate-pulse-glow`}>
                                  <StageIcon className="w-4 h-4 text-white" />
                                </div>
                              ) : (
                                <div className="w-7 h-7 bg-gray-100 rounded-lg flex items-center justify-center">
                                  <StageIcon className="w-4 h-4 text-gray-300" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium transition-colors duration-300 ${
                                isComplete ? 'text-gray-900' : isActive ? 'text-gray-900' : 'text-gray-400'
                              }`}>
                                {stage.title}
                              </p>
                              {isActive && (
                                <p className="text-xs text-gray-500 mt-0.5 animate-fade-in-up">
                                  {progress?.message || stage.description}
                                </p>
                              )}
                            </div>
                            {isComplete && (
                              <span className="text-xs text-gray-900 font-semibold">Done</span>
                            )}
                            {isActive && !isComplete && (
                              <div className={`w-2 h-2 rounded-full bg-gradient-to-br ${config.gradient} animate-pulse`} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl border border-gray-100 p-4 mb-6">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Did you know?</p>
                    <p className="text-sm text-gray-600 leading-relaxed min-h-[40px]">
                      {config.tips[tipIndex]}
                    </p>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-1.5 text-xs text-gray-400">
                      <Clock className="w-3.5 h-3.5" />
                      <span>Elapsed: {formatTime(elapsedTime)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-400">
                      <Shield className="w-3.5 h-3.5" />
                      <span>Processing continues if you switch tabs</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="hidden lg:block lg:col-span-2 animate-fade-in-up">
              <div className="rounded-2xl overflow-hidden shadow-elevated-lg border border-gray-200">
                <div className="relative h-[600px]">
                  <img
                    src={config.heroImage}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${config.overlayGradient}`} />
                  <div className="absolute inset-0 flex flex-col justify-end p-8">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${config.gradient} flex items-center justify-center mb-4 shadow-lg`}>
                      <ServiceIcon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-3 leading-tight">
                      {config.heroTagline}
                    </h3>
                    <div className="space-y-2.5">
                      {(serviceType === 'commercial_lease_review' ? commercialStages : tenancyStages).map((stage, index) => {
                        const isComplete = index < currentStage || (index === currentStage && progress?.status === 'complete');
                        const isActive = index === currentStage;
                        return (
                          <div key={index} className="flex items-center gap-2.5">
                            {isComplete ? (
                              <CheckCircle className="w-4 h-4 text-white flex-shrink-0" />
                            ) : isActive ? (
                              <div className={`w-4 h-4 rounded-full bg-gradient-to-br ${config.gradient} animate-pulse flex-shrink-0`} />
                            ) : (
                              <div className="w-4 h-4 rounded-full border border-white/30 flex-shrink-0" />
                            )}
                            <span className={`text-sm ${isComplete ? 'text-white' : isActive ? 'text-white font-medium' : 'text-white/40'}`}>
                              {stage.title}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-8 pt-6 border-t border-white/15">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5 text-white/50 text-xs">
                          <Shield className="w-3.5 h-3.5" />
                          <span>Secure Processing</span>
                        </div>
                        <div className="w-px h-3 bg-white/20" />
                        <div className="flex items-center gap-1.5 text-white/50 text-xs">
                          <Clock className="w-3.5 h-3.5" />
                          <span>{formatTime(elapsedTime)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
